import { Injectable } from '@angular/core';
import { filterNil } from '@datorama/akita';
import {
	ComponentController,
	filterEmpty,
	filterNull,
	localeDateToDateTime,
} from '@lycoris-nubila/ngx-lycoris';
import { DateTime } from 'luxon';
import { SelectItem } from 'primeng/api';
import { Observable, combineLatest } from 'rxjs';
import {
	debounceTime,
	filter,
	finalize,
	first,
	map,
	shareReplay,
	switchMap,
} from 'rxjs/operators';

import {
	AddCoachMeetingDialogStore,
	CoacheesAddMeetingState,
} from 'dialog/add-coach-meeting-dialog/add-coach-meeting-dialog.store';
import { ModuleSort } from 'module/model/module.sort';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { TaskApplicationPort } from 'task/port/task-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

@Injectable()
export class AddCoachMeetingDialogController extends ComponentController<CoacheesAddMeetingState> {
	public static BASE_I18N_KEY = 'presentation.app.common.dialog.addCoachMeeting';

	public coachee$: Observable<IUser>;
	public meetingDate$: Observable<Date>;
	public coacheeDateTime$: Observable<DateTime>;
	public modules$: Observable<SelectItem[]>;
	public isSaving$: Observable<boolean>;

	constructor(
		store: AddCoachMeetingDialogStore,
		private _userApplicationPort: UserApplicationPort,
		private _taskApplicationPort: TaskApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort
	) {
		super(store);

		this._initObservables();
	}

	public updateMeetingDate(meetingDate: Date): void {
		this.store.update({ meetingDate });
	}

	public updateModulesId(modulesId: string[]): void {
		this.store.update({ modulesId });
	}

	public updateCoacheeId(coacheeId: string) {
		this.store.update({ coacheeId });
	}

	private _initObservables() {
		this.meetingDate$ = this.select('meetingDate').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		const sessionUser$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.coachee$ = this.select('coacheeId').pipe(
			filterEmpty(),
			switchMap((coacheeId) =>
				this._userApplicationPort.observeUserById(coacheeId)
			),
			map((data) => data.data),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.coacheeDateTime$ = combineLatest([
			sessionUser$.pipe(
				filterNull(),
				map((user) => user.timeZone)
			),
			this.coachee$.pipe(
				filterNull(),
				map((user) => user.timeZone)
			),
			this.select('meetingDate').pipe(filterNull()),
		]).pipe(
			map(([sourceTimeZone, targetTimeZone, meetingDate]) =>
				localeDateToDateTime(meetingDate, sourceTimeZone).setZone(targetTimeZone)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.modules$ = this.coachee$.pipe(
			filterNil,
			switchMap((user) =>
				this._moduleApplicationPort
					.observeModules({
						organizationId: user.organizationId,
						sort: ModuleSort.NAME,
					})
					.pipe(
						filter((data) => !!data.data),
						map((data) => data.data),
						switchMap((modules) =>
							combineLatest(
								modules.map((module) =>
									module.localization$.pipe(
										map(
											(loc) =>
												({
													label: loc.name,
													value: module.id,
												} as SelectItem)
										)
									)
								)
							)
						)
					)
			),
			debounceTime(100),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSaving$ = this.select('saving').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public addMeeting(): Observable<void> {
		this.store.update({ saving: true });
		return combineLatest([this.coachee$, this.coacheeDateTime$]).pipe(
			first(),
			switchMap(([coachee, coacheeDateTime]) =>
				this._taskApplicationPort.createCoachMeeting({
					dueTimestamp: coacheeDateTime.toMillis(),
					coacheesId: [coachee.id],
					modulesId: this.store.getValue().modulesId,
					canCoacheesBeNotified: false,
				})
			),
			finalize(() => {
				this.store.update({
					saving: false,
				});
			})
		);
	}
}
